import type {
  CSSProperties,
  PointerEventHandler,
  RefObject,
  WheelEventHandler,
} from 'react';
import type { Room } from 'livekit-client';
import type { ViewportBoundingBox } from '@/app/Teleop/teleopConfig';
import type { ViewportProfile } from '@/lib/videoFraming';
import VideoDebugOverlay from '@/components/teleop/VideoDebugOverlay';
import LiveKitOperatorProvider from '@/providers/LiveKitOperatorProvider';

type TeleopVideoSurfaceProps = {
  confettiLayerRef: RefObject<HTMLDivElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoStyle: CSSProperties;
  onVideoPointerDown: PointerEventHandler<HTMLVideoElement>;
  onVideoPointerUp: PointerEventHandler<HTMLVideoElement>;
  onVideoPointerCancel: PointerEventHandler<HTMLVideoElement>;
  onVideoWheel: WheelEventHandler<HTMLVideoElement>;
  livekitRoom: string;
  activeCamera: string;
  onStatusChange: (
    status: 'idle' | 'connecting' | 'connected' | 'failed',
  ) => void;
  onTracksChange: (tracks: string[]) => void;
  onActiveTrackChange: (trackName: string) => void;
  onRoom: (room: Room | null) => void;
  videoDebugEnabled: boolean;
  viewportWidthPx: number;
  viewportHeightPx: number;
  viewportProfile: ViewportProfile;
  criticalBoundingBox: ViewportBoundingBox;
  expectedStyle: CSSProperties;
};

const VIDEO_SURFACE_BACKGROUND =
  'radial-gradient(circle at 20% 80%, rgba(69, 240, 223, 0.2), transparent 45%), linear-gradient(180deg, rgba(2, 4, 10, 0.2) 0%, rgba(2, 4, 10, 0.7) 70%)';

const TeleopVideoSurface: React.FC<TeleopVideoSurfaceProps> = ({
  confettiLayerRef,
  videoRef,
  videoStyle,
  onVideoPointerDown,
  onVideoPointerUp,
  onVideoPointerCancel,
  onVideoWheel,
  livekitRoom,
  activeCamera,
  onStatusChange,
  onTracksChange,
  onActiveTrackChange,
  onRoom,
  videoDebugEnabled,
  viewportWidthPx,
  viewportHeightPx,
  viewportProfile,
  criticalBoundingBox,
  expectedStyle,
}) => {
  const hasLivekitRoom = livekitRoom.length > 0;

  return (
    <>
      <div
        ref={confettiLayerRef}
        className="pointer-events-none absolute inset-0 z-20"
      />
      <video
        ref={videoRef}
        className="absolute"
        style={videoStyle}
        autoPlay
        playsInline
        muted
        onPointerDown={onVideoPointerDown}
        onPointerUp={onVideoPointerUp}
        onPointerCancel={onVideoPointerCancel}
        onWheel={onVideoWheel}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: VIDEO_SURFACE_BACKGROUND }}
      />
      {hasLivekitRoom ? (
        <LiveKitOperatorProvider
          roomName={livekitRoom}
          videoRef={videoRef}
          onStatusChange={onStatusChange}
          activeTrackName={activeCamera}
          onTracksChange={onTracksChange}
          onActiveTrackChange={onActiveTrackChange}
          onRoom={onRoom}
        />
      ) : null}
      <VideoDebugOverlay
        enabled={videoDebugEnabled}
        videoRef={videoRef}
        viewportWidthPx={viewportWidthPx}
        viewportHeightPx={viewportHeightPx}
        viewportProfile={viewportProfile}
        criticalBoundingBox={criticalBoundingBox}
        expectedStyle={expectedStyle}
      />
    </>
  );
};

export default TeleopVideoSurface;
